import SelectController from "@/common/components/SelectController"
import { PrivateCompanyLeadsFunnelUpdateValidationSchema } from "@/features/privateCompanyLeadsFunnel/schemas/update.schema"
import { Control, FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch } from "react-hook-form"
import { options as monthDaysOptions } from '@/features/privateCompanyLeadsFunnel/constants/monthDaysOptions.constant'
import { options as weekDaysOptions } from '@/features/privateCompanyLeadsFunnel/constants/weekDaysOptions.constants'
import { options as cycleOptions } from '@/features/privateCompanyLeadsFunnel/constants/cycleOptions.constant'
import MaskedTextField from "@/common/components/MaskedTextField";
import { BaseSyntheticEvent, useMemo } from "react"
import { ContractData } from "../types/privateCompanyLead"
import { InfoIcon } from "lucide-react"

interface BillDetailsSectionProps {
  contract?: ContractData,
  control: Control<PrivateCompanyLeadsFunnelUpdateValidationSchema>
  watch: UseFormWatch<PrivateCompanyLeadsFunnelUpdateValidationSchema>
  setValue?: UseFormSetValue<PrivateCompanyLeadsFunnelUpdateValidationSchema>
  register: UseFormRegister<PrivateCompanyLeadsFunnelUpdateValidationSchema>
  errors?: FieldErrors<PrivateCompanyLeadsFunnelUpdateValidationSchema>
  submit?: (e?: BaseSyntheticEvent) => Promise<void>
  submittableOnBlur?: (e: React.FocusEvent<HTMLInputElement, Element>, onBlur: (e: React.FocusEvent<HTMLInputElement, Element>) => void) => void
  disabled?: boolean
}

export const BillDetailsSection = ({
  contract,
  control,
  watch,
  setValue,
  register,
  errors,
  submit,
  submittableOnBlur,
  disabled
}: BillDetailsSectionProps) => {
  const getCycleStartDayDefaultValue = useMemo(() => {
    return contract?.cycle === 'weekly'
      ? (contract?.cycle_start_day !== null && contract?.cycle_start_day !== undefined) ? weekDaysOptions[contract?.cycle_start_day] : ''
      : monthDaysOptions.find(({ value }) => value === `${contract?.cycle_start_day}`);
  }, [contract?.cycle]);

  const watchCycle = watch('contract.cycle')

  return (
    <div className="flex flex-col items-start gap-y-4 pb-1">
      <div className="flex flex-col">
        <p className="text-[18px] font-bold">Detalhes da fatura</p>
        <span className="flex items-center gap-1 text-xs">
          <InfoIcon strokeWidth={1} className="text-yellow-600" />
          O ciclo da fatura poderá ou não ser alterado em outras etapas do funil.
        </span>
      </div>
      <div className="flex w-full gap-2">
        <SelectController
          control={control}
          name="contract.cycle"
          label="Ciclo de fatura"
          options={cycleOptions}
          className="flex-1"
          triggerOnChange={({ value }) => {
            setValue?.('contract.cycle_start_day', value === 'weekly' ? weekDaysOptions[0] : monthDaysOptions[0])
            submit?.()
          }}
          defaultValue={cycleOptions.find((cycle) => cycle.value === contract?.cycle)}
          isDisabled={disabled}
          errorMessage={errors?.contract?.cycle?.message}
        />
        {watchCycle?.value === 'fixed' && (
          <MaskedTextField
            label="Intervalo de ciclo fixo"
            placeholder="Quantidade em dias"
            mask='0000'
            className="flex-1"
            defaultValue={contract?.cycle_fixed_interval}
            {...register('contract.cycle_fixed_interval')}
            onBlur={(e) => submittableOnBlur && submittableOnBlur(e, register('contract.cycle_fixed_interval').onBlur)}
            errorMessage={errors?.contract?.cycle_fixed_interval?.message || undefined}
            disabled={disabled}
          />
        )}
        {watchCycle?.value !== 'fixed' && (
          <SelectController
            control={control}
            name="contract.cycle_start_day"
            label="Dia de abertura"
            className="flex-1"
            defaultValue={getCycleStartDayDefaultValue}
            triggerOnChange={submit}
            options={watchCycle?.value === 'weekly' ? weekDaysOptions : monthDaysOptions(28)}
            isDisabled={disabled}
            errorMessage={errors?.contract?.cycle_start_day?.message}
          />
        )}
      </div>
    </div>
  )
}
